import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useDeferredReady } from "../../hooks/useDeferredReady";
import type { SkillCollectionReviewItem } from "../../store";

interface UseVersionReviewStateParams {
  isOpen: boolean;
  reviewItems: SkillCollectionReviewItem[];
  versionFiles: Record<string, string> | null;
  isLoadingVersionSnapshot: boolean;
  onConfirm: (changeSummary?: string) => void;
}

export const useVersionReviewState = ({
  isOpen,
  reviewItems,
  versionFiles,
  isLoadingVersionSnapshot,
  onConfirm,
}: UseVersionReviewStateParams) => {
  const [changeSummary, setChangeSummary] = useState("");
  const [originalContentByPath, setOriginalContentByPath] = useState<Record<string, string>>({});
  const isDiffsReady = useDeferredReady(isOpen);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onConfirm(changeSummary.trim() || undefined);
    },
    [changeSummary, onConfirm],
  );

  const originalsFromVersion = useMemo(() => {
    if (!versionFiles || !isOpen) {
      return {};
    }
    const out: Record<string, string> = {};
    reviewItems
      .filter(item => item.requiresOriginalFetch)
      .forEach(item => {
        const path = item.oldPath ?? item.path;
        if (path && path in versionFiles) {
          out[path] = versionFiles[path];
        }
      });
    return out;
  }, [isOpen, reviewItems, versionFiles]);

  const needsVersionFileMap = useMemo(
    () =>
      reviewItems.some(item => {
        if (!item.requiresOriginalFetch || item.originalContent !== undefined) {
          return false;
        }
        const path = item.oldPath ?? item.path;
        return Boolean(path);
      }),
    [reviewItems],
  );

  useEffect(() => {
    if (!isOpen) {
      setChangeSummary(previousSummary => (previousSummary ? "" : previousSummary));
      setOriginalContentByPath(previousPaths =>
        Object.keys(previousPaths).length > 0 ? {} : previousPaths,
      );
      return;
    }

    setOriginalContentByPath(prev => ({ ...prev, ...originalsFromVersion }));
  }, [isOpen, originalsFromVersion]);

  const isLoadingOriginals =
    isOpen && needsVersionFileMap && (isLoadingVersionSnapshot || versionFiles === null);

  return {
    changeSummary,
    setChangeSummary,
    originalContentByPath,
    isDiffsReady,
    isLoadingOriginals,
    handleSubmit,
  };
};
